/**
 * Mutations de vídeo com atualização otimista (mesmo padrão dos posts):
 * - useToggleVideoLike: coração + votes_count instantâneos, com rollback.
 * - useCreateVideo: invalida o feed para o novo vídeo aparecer.
 * - useUserVideoLikes: conjunto de IDs curtidos (estado "is liked").
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  toggleVideoLike,
  fetchUserVideoLikes,
  createYoutubeVideo,
  uploadCommunityVideo,
  deleteCommunityVideo,
} from '../services/communityVideosService';
import { videoKeys } from '../constants';

const FEED_PREFIX = [...videoKeys.all, 'feed'];

/** Remove um vídeo (por id) de qualquer cache paginado ({ pages: [{ items }] }). */
const removeVideoFromPages = (data, videoId) => {
  if (!data?.pages) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: (page.items ?? []).filter((v) => v.id !== videoId),
    })),
  };
};

const bumpVotesInPages = (data, videoId, delta) => {
  if (!data?.pages) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: (page.items ?? []).map((v) =>
        v.id === videoId ? { ...v, votesCount: Math.max(0, v.votesCount + delta) } : v
      ),
    })),
  };
};

export const useUserVideoLikes = (enabled = true) =>
  useQuery({
    queryKey: videoKeys.userLikes(),
    queryFn: fetchUserVideoLikes,
    staleTime: 60 * 1000,
    enabled,
  });

export const useToggleVideoLike = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ videoId, liked }) => toggleVideoLike(videoId, liked),
    onMutate: async ({ videoId, liked }) => {
      await queryClient.cancelQueries({ queryKey: videoKeys.userLikes() });
      const prevLikes = queryClient.getQueryData(videoKeys.userLikes());

      queryClient.setQueryData(videoKeys.userLikes(), (old = []) =>
        liked ? [...new Set([...old, videoId])] : old.filter((id) => id !== videoId)
      );
      queryClient.setQueriesData({ queryKey: FEED_PREFIX }, (data) =>
        bumpVotesInPages(data, videoId, liked ? 1 : -1)
      );

      return { prevLikes, videoId, liked };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      queryClient.setQueryData(videoKeys.userLikes(), ctx.prevLikes ?? []);
      queryClient.setQueriesData({ queryKey: FEED_PREFIX }, (data) =>
        bumpVotesInPages(data, ctx.videoId, ctx.liked ? -1 : 1)
      );
    },
  });
};

// onSettled (não onSuccess): num timeout o insert pode ter persistido sem
// resposta — invalidar também no erro reconcilia o feed com o que de fato entrou.
export const useCreateVideo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createYoutubeVideo,
    onSettled: () => queryClient.invalidateQueries({ queryKey: videoKeys.all }),
  });
};

export const useUploadVideo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadCommunityVideo,
    onSettled: () => queryClient.invalidateQueries({ queryKey: videoKeys.all }),
  });
};

/**
 * Apaga um vídeo do usuário (autorização real na RLS). Otimista: remove de TODOS
 * os caches de vídeo (perfil + feeds) com rollback; `onSettled` reconcilia.
 */
export const useDeleteVideo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (video) => deleteCommunityVideo(video),
    onMutate: async (video) => {
      await queryClient.cancelQueries({ queryKey: videoKeys.all });
      const previous = queryClient.getQueriesData({ queryKey: videoKeys.all });
      queryClient.setQueriesData({ queryKey: videoKeys.all }, (data) =>
        removeVideoFromPages(data, video.id)
      );
      return { previous };
    },
    onError: (_err, _video, ctx) => {
      ctx?.previous?.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: videoKeys.all }),
  });
};
